/*
 * header-search.js — SearchSpring-style header autocomplete (replacement).
 * Binds to the header search input(s) (.searchspring-query). On first focus it lazy-loads the
 * lightweight global search index from the app (App Proxy), then on input shows a dropdown with:
 *   - term suggestions (manufacturer-prefixed, derived from matches)
 *   - a product-results grid (image / name / part# / price)
 *   - a "See all N results for '…'" link -> native /search?q=
 * Enter / See-all submit to /search (native results page). Nothing hard-coded; data is live.
 */
(function () {
  'use strict';
  var CFG = window.FACET_SEARCH_CFG || {};
  var INDEX = null, loading = null;
  var MAX_ITEMS = 8, MAX_TERMS = 5;

  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function money(d) { return (CFG.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, Number(d || 0).toFixed(2)); }
  function searchUrl(q) { return (CFG.searchUrl || '/search') + '?q=' + encodeURIComponent(q); }

  function loadIndex() {
    if (INDEX && INDEX.length) return Promise.resolve(INDEX);
    if (loading) return loading;
    // use the cache ONLY if it's non-empty — this self-heals a cache poisoned by an earlier
    // failed/empty fetch (e.g. during a backend outage), which otherwise sticks for the session
    try { var cached = sessionStorage.getItem('fs_search_index'); if (cached) { var arr = JSON.parse(cached); if (arr && arr.length) { INDEX = arr; return Promise.resolve(INDEX); } } } catch (e) {}
    loading = fetch((CFG.endpoint || '/apps/facets/index.json') + '?collection=__search__', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('search index ' + r.status); return r.json(); })
      .then(function (d) {
        INDEX = (d && d.records) || [];
        // only cache a real, non-empty index so a transient failure never poisons the cache
        if (INDEX.length) { try { sessionStorage.setItem('fs_search_index', JSON.stringify(INDEX)); } catch (e) {} }
        return INDEX;
      })
      .catch(function () { INDEX = []; loading = null; return INDEX; });   // allow retry on next focus/keystroke
    return loading;
  }

  function search(q) {
    var ql = q.toLowerCase().trim();
    var toks = ql.split(/\s+/).filter(Boolean);
    if (!toks.length) return [];
    var out = [];
    for (var i = 0; i < INDEX.length; i++) {
      var r = INDEX[i];
      if (!(Number(r.price) > 0)) continue;   // never surface unpriced ($0) products (backstop vs stale cache)
      var sku = (r.sku || '').toLowerCase();
      var name = ((r.title || '') + ' ' + (r.variant_title || '')).toLowerCase();
      var oems = (r.oems || []).join(' ').toLowerCase();
      var hay = name + ' ' + sku + ' ' + oems + ' ' + (r.manufacturer || []).join(' ').toLowerCase();
      var ok = true; for (var t = 0; t < toks.length; t++) { if (hay.indexOf(toks[t]) < 0) { ok = false; break; } }
      if (!ok) continue;
      // SKU is prioritised: exact SKU < SKU prefix < SKU contains < SKU token < OEM < name < other
      var sc;
      if (sku === ql) sc = 0;
      else if (sku.indexOf(ql) === 0) sc = 1;
      else if (sku.indexOf(ql) >= 0) sc = 2;
      else if (toks.some(function (tk) { return sku.indexOf(tk) >= 0; })) sc = 3;
      else if (oems.indexOf(ql) >= 0) sc = 4;
      else if (name.indexOf(ql) >= 0) sc = 5;
      else sc = 6;
      out.push({ r: r, sc: sc });
    }
    out.sort(function (a, b) { return a.sc - b.sc || (a.r.sku || '').localeCompare(b.r.sku || ''); });
    return out.map(function (x) { return x.r; });
  }

  function suggestions(q, results) {
    var mans = {}, list = [];
    results.forEach(function (r) { (r.manufacturer || []).forEach(function (m) { var top = m.split(' > ')[0]; mans[top] = (mans[top] || 0) + 1; }); });
    Object.keys(mans).sort(function (a, b) { return mans[b] - mans[a]; }).slice(0, MAX_TERMS - 1).forEach(function (m) { list.push((m + ' ' + q).toLowerCase()); });
    return [q].concat(list);
  }

  function dropdownHTML(q, results) {
    var total = results.length;
    var items = results.slice(0, MAX_ITEMS);
    var terms = suggestions(q, results);
    var termsHTML = terms.map(function (t, i) { return '<a class="fs-ac-term' + (i === 0 ? ' active' : '') + '" href="' + esc(searchUrl(t)) + '">' + esc(t) + '</a>'; }).join('');
    var ph = CFG.placeholderImage || '';
    var imgErr = ph ? ' onerror="this.onerror=null;this.src=\'' + ph + '\'"' : '';
    var itemsHTML = items.map(function (r) {
      var name = r.title + (r.variant_title ? ' - ' + r.variant_title : '');
      return '<a class="fs-ac-item" href="/products/' + esc(r.handle) + '?variant=' + esc(r.id) + '">' +
        '<span class="fs-ac-img"><img src="' + esc(r.image || ph) + '" alt="" loading="lazy"' + imgErr + '></span>' +
        '<span class="fs-ac-name">' + esc(name) + '</span>' +
        '<span class="fs-ac-sku">' + esc(r.sku || '') + '</span>' +
        (CFG.showPrices !== false && Number(r.price) > 0 ? '<span class="fs-ac-price">' + money(r.price) + '</span>' : '') + '</a>';
    }).join('');
    return '<div class="fs-ac-terms">' + termsHTML + '</div>' +
      '<div class="fs-ac-grid">' + (itemsHTML || '<p class="fs-ac-empty">No matches.</p>') + '</div>' +
      (total ? '<a class="fs-ac-seeall" href="' + esc(searchUrl(q)) + '">See all ' + total.toLocaleString() + ' results for &ldquo;' + esc(q) + '&rdquo;</a>' : '');
  }

  function attach(input) {
    var box = document.createElement('div');
    box.className = 'fs-ac'; box.style.display = 'none';
    document.body.appendChild(box);

    function position() {
      var form = input.closest('form') || input;
      var r = (form.querySelector('.input-group, .form-searchbar') || input).getBoundingClientRect();
      box.style.position = 'absolute';
      box.style.top = (window.scrollY + r.bottom + 2) + 'px';
      box.style.left = (window.scrollX + r.left) + 'px';
      box.style.width = Math.max(r.width, 640) + 'px';
      box.style.zIndex = 4000;
    }
    function show() { position(); box.style.display = 'block'; }
    function hide() { box.style.display = 'none'; }

    var timer;
    function run() {
      var q = input.value.trim();
      if (q.length < 2) { hide(); return; }
      loadIndex().then(function () {
        var results = search(q);
        box.innerHTML = dropdownHTML(q, results);
        show();
      });
    }
    input.addEventListener('focus', function () { loadIndex(); if (input.value.trim().length >= 2) run(); });
    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(run, 180); });
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
    document.addEventListener('click', function (e) { if (e.target !== input && !box.contains(e.target)) hide(); });
    window.addEventListener('resize', function () { if (box.style.display === 'block') position(); });
  }

  function init() {
    var inputs = document.querySelectorAll('input.searchspring-query, form[action="/search"] input[name="q"]');
    Array.prototype.forEach.call(inputs, attach);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
